<script>
import debounce from 'lodash/debounce';
import { v1 as uuid } from 'uuid';
import { bus } from '../../main';

export default {
  name: 'ToggleTip',
  props: {
    open: {
      type: String,
      required: true,
    },
    close: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    description: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      bus,
      id: uuid(),
      isActive: false,
      offset: 0,
    };
  },
  computed: {
    styles() {
      return {
        marginLeft: `${Math.ceil(this.offset)}px`,
      };
    },
  },
  created() {
    bus.$on('toggle-tip:hide', (id) => {
      if (id !== this.id) this.hide();
    });
    document.addEventListener('click', (e) => {
      if (this.$el.contains(e.target) || e.target === this.$el) return;
      this.hide();
    });
    window.addEventListener('resize', debounce(() => this.update(), 300));
  },
  methods: {
    // visibility

    hide() {
      if (!this.isActive) return;
      this.isActive = false;
      document.body.classList.remove('modal-opened');
      this.$nextTick(() => this.focusOpen());
    },
    show() {
      if (this.isActive) return;
      this.isActive = true;
      bus.$emit('toggle-tip:hide', this.id);
      this.update();
      document.body.classList.add('modal-opened');
      this.$nextTick(() => this.focusClose());
    },

    onMouseEnter() {
      // if the device is touch, skip
      if (matchMedia('(hover: none)').matches) return;

      this.show();
    },

    // focus management

    focusClose() {
      this.$refs.close.focus();
    },
    focusOpen() {
      this.$refs.open.focus();
    },

    // position

    update() {
      const { dialog } = this.$refs;
      const closestContainer = this.$el.closest('.container, .container-fluid, body');

      this.$nextTick(() => {
        const leftLimit = dialog.getBoundingClientRect().left - closestContainer.getBoundingClientRect().left - 16;
        const rightLimit = dialog.getBoundingClientRect().right - closestContainer.getBoundingClientRect().right + 16;

        if (leftLimit < 0) this.offset += Math.abs(leftLimit);
        else if (rightLimit > 0) this.offset -= Math.abs(rightLimit);
      });
    },
  },
};
</script>
<template>
  <div
    class="toggle-tip"
    :class="{ 'is-active': isActive }"
    @keydown.esc="hide"
  >
    <span
      ref="open"
      :aria-describedby="isActive ? `${id}-dialog` : false"
      class="toggle-tip__open"
      role="button"
      tabindex="0"
      @click="show"
      @keydown.enter.prevent="show"
      @keyup.space.prevent="show"
      @mouseenter="onMouseEnter"
      v-html="open"
    />

    <div
      class="toggle-tip__modal"
      :style="styles"
      @click="hide"
    >
      <div class="toggle-tip__container">
        <div
          :id="`${id}-dialog`"
          :aria-labelledby="`${id}-title`"
          :aria-hidden="!isActive"
          class="toggle-tip__dialog"
          ref="dialog"
          role="dialog"
          @click.stop
        >
          <div
            class="toggle-tip__guard"
            tabindex="0"
            @focus="focusClose"
          />

          <button
            class="toggle-tip__close"
            type="button"
            ref="close"
            @click="hide"
          >
            <svg viewBox="0 0 13 13" xmlns="http://www.w3.org/2000/svg"><path d="M7.881 6.501l4.834-4.834c.38-.38.38-1.001 0-1.381s-1.001-.38-1.381 0L6.5 5.12 1.666.285c-.38-.38-1.001-.38-1.381 0s-.38 1.001 0 1.381L5.119 6.5.285 11.334c-.38.38-.38 1.001 0 1.381s1.001.38 1.381 0L6.5 7.881l4.834 4.834c.38.38 1.001.38 1.381 0s.38-1.001 0-1.381z" fill="currentColor"/></svg>
            <div class="sr-only">
              {{ close }}
            </div>
          </button>
          <div
            :id="`${id}-title`"
            class="toggle-tip__title h4"
            v-html="title"
          />

          <div
            class="toggle-tip__description wysiwyg"
            v-html="description"
          />

          <div
            class="toggle-tip__guard"
            tabindex="0"
            @focus="focusClose"
          />
        </div>
      </div>
    </div>
  </div>
</template>
